import {
  Box,
  Divider,
  Flex,
  IconButton,
  Text,
  useToast,
} from "@chakra-ui/react";
import { DotsThreeVertical, Plus } from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "config";
import { Issb } from "../../../../api";
import { useGetStagesOfRound, useGetAllAudit } from "../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { Fragment } from "react";
import DevOnly from "context/Auth/DevOnly";
import useAirUserRoles from "utils/AuthUtils";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import questionnaireService from "../../../../api/issb/questionnaire.service";
import { useRefreshRoundList, useSelectRound } from "../../QuestionnaireRound/RoundService";
import { useTranslation } from "react-i18next";

/**
 * Kebab button that toggles the actions panel
 */
export const ActionListButton = ({ isOpen, onToggle }) => {
  return (
    <IconButton
      variant="ghost"
      icon={<DotsThreeVertical size={20} weight="bold" />}
      h="44px"
      color="darkTeal"
      borderRadius={0}
      _hover={{ bg: "transparent" }}
      _active={{ bg: "transparent" }}
      aria-label="Actions"
      onClick={onToggle}
    />
  );
};

/**
 * Panel item row
 */
const PanelItem = ({ icon, children, onClick, isDisabled, title, color }) => (
  <Flex
    as="button"
    align="center"
    gap={3}
    pl="44px"
    pr={4}
    py={2}
    w="100%"
    fontSize="sm"
    color={isDisabled ? "gray.400" : color || "gray.700"}
    cursor={isDisabled ? "not-allowed" : "pointer"}
    _hover={isDisabled ? {} : { bg: "gray.50" }}
    onClick={isDisabled ? undefined : onClick}
    title={title}
    disabled={isDisabled}
  >
    {icon && <Box flexShrink={0}>{icon}</Box>}
    <Text>{children}</Text>
  </Flex>
);

/**
 * Section header in the panel
 */
const PanelSection = ({ title }) => (
  <Text pl="44px" pr={4} pt={3} pb={1} fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wider">
    {title}
  </Text>
);

/**
 * Actions panel content displayed inline below the sub-header
 */
export const ActionPanelContent = ({ onClose }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { refreshRoundList } = useRefreshRoundList();
  const selectRound = useSelectRound();

  const { t } = useTranslation("translation", {
    keyPrefix: "issb",
  });
  const { isController, isOrgAdmin } = useAirUserRoles();

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = roundSelector.selectedRound?.id;

  const { data: stagesData } = useGetStagesOfRound(questionnaireId, {
    enabled: !!questionnaireId,
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const auditStages = (stagesData || []).filter((stage) => stage.stageType === "AUDIT");

  const { data: allAudits } = useGetAllAudit(questionnaireId, {
    enabled: !!questionnaireId,
  });

  const activeAuditStageNames = new Set(
    (allAudits || []).filter((a) => a.isActive).map((a) => a.stageName)
  );

  const validateControl = () => {
    const currentRound = roundSelector?.selectedRound;
    if (!currentRound) {
      toast({
        title: "Error",
        description: "Please select a round to start an audit",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleStartAudit = (stage) => {
    if (!validateControl()) return;
    onClose();
    navigate(`${routes.issbStartAudit}?stageId=${stage?.id}&stageName=${stage?.name}`);
  };

  const handleCloseAudit = (stage) => {
    if (!validateControl()) return;
    onClose();
    navigate(`${routes.issbCloseAudit}?stageId=${stage?.id}&stageName=${stage?.name}`);
  };

  const handleConfigureStage = () => {
    onClose();
    navigate(routes.issbStageSetup);
  };

  const handleAIReview = () => {
    onClose();
    navigate(routes.aiReview);
  };

  const handleDeleteRound = () => {
    console.warn("Delete round!", roundSelector?.selectedRound);
    Issb.deleteQuestionnaireRound(roundSelector?.selectedRound.id);
    onClose();
  };

  const getRoundActionMenuLabel = () => {
    const status = roundSelector?.selectedRound?.state;
    switch (status) {
      case "DRAFT":
        return "Start Round";
      case "CLOSED":
        return "Reopen Round";
      case "ACTIVE":
        return "Close Round";
      default:
        return "Start Round";
    }
  };

  const handleStartCloseRound = () => {
    const selectedRound = roundSelector?.selectedRound;
    if (!selectedRound) return;
    const qround = { ...selectedRound };
    switch (selectedRound?.state) {
      case "DRAFT":
        qround.state = "ACTIVE";
        break;
      case "ACTIVE":
        qround.state = "CLOSED";
        break;
      case "CLOSED":
        qround.state = "ACTIVE";
        break;
      default:
        qround.state = "ACTIVE";
        break;
    }

    dispatch(showAppSpinner());
    questionnaireService
      .create(qround)
      .then((qround2) => {
        refreshRoundList().then(() => {
          selectRound(qround2);
          showSuccessToast(toast, t("general.success"));
          onClose();
          setTimeout(() => {
            navigate(routes.issbRequirements);
          }, 500);
        });
      })
      .catch((err) => {
        console.error(err);
        showErrorToast(toast, t("general.error"));
      })
      .finally(() => {
        dispatch(hideAppSpinner());
      });
  };

  let auditDisabledReason = null;
  if (!isController()) auditDisabledReason = "Only controllers can start audits";
  else if (!roundSelector?.selectedRound) auditDisabledReason = "Please select a round";
  else if (roundSelector?.selectedRound?.state === "DRAFT") auditDisabledReason = "First start the Round";

  const handleCreateRound = () => {
    onClose();
    navigate(routes.issbQuestionnaireRoundSetup);
  };

  return (
    <Box>
      {isOrgAdmin && (
        <Flex
          as="button"
          align="center"
          gap={3}
          px={4}
          py={3}
          w="100%"
          fontSize="sm"
          fontWeight="500"
          color="darkTeal"
          bg="lightLime"
          cursor="pointer"
          _hover={{ bg: "lightLime" }}
          onClick={handleCreateRound}
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Plus size={16} weight="bold" />
          <Text>Create a new round</Text>
        </Flex>
      )}
      <Box py={2}>
      <PanelItem
        isDisabled={!roundSelector?.selectedRound || !isController()}
        title={isController() ? "Start Round" : "Only controllers can start or close rounds"}
        onClick={handleStartCloseRound}
      >
        {getRoundActionMenuLabel()}
      </PanelItem>
      <PanelItem
        onClick={handleConfigureStage}
        isDisabled={!roundSelector?.selectedRound || !isController()}
      >
        Configure Stages
      </PanelItem>
      <PanelItem
        onClick={() => { onClose(); navigate(`${routes.issbQuestionnaireRoundSetup}?edit=true`); }}
        isDisabled={!roundSelector?.selectedRound || (!isController() && !isOrgAdmin)}
      >
        Edit Round
      </PanelItem>
      <DevOnly>
        <PanelItem
          onClick={handleDeleteRound}
          isDisabled={!roundSelector?.selectedRound || (!isController() && !isOrgAdmin)}
          title={isController() ? "Delete Round" : "Only controllers can delete rounds"}
          color="red.500"
        >
          Delete this round
        </PanelItem>
      </DevOnly>

      <Divider my={1} />
      <PanelSection title="AI" />
      <PanelItem
        onClick={handleAIReview}
        isDisabled={!roundSelector?.selectedRound}
      >
        AI Review
      </PanelItem>

      <Divider my={1} />
      <PanelSection title="Audits" />
      {auditStages.map((stage) => {
        const isRunning = activeAuditStageNames.has(stage?.name);
        return (
          <Fragment key={stage?.id}>
            {isRunning ? (
              <PanelItem
                onClick={() => handleCloseAudit(stage)}
                isDisabled={!!auditDisabledReason}
                title={auditDisabledReason || "Close Audit: " + stage?.name}
              >
                Close {stage?.name}
              </PanelItem>
            ) : (
              <PanelItem
                onClick={() => handleStartAudit(stage)}
                isDisabled={!!auditDisabledReason}
                title={auditDisabledReason || "Start Audit: " + stage?.name}
              >
                Start {stage?.name}
              </PanelItem>
            )}
          </Fragment>
        );
      })}
    </Box>
    </Box>
  );
};

export default ActionListButton;
